////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                                                                                                //
//                                           매스컨셉 임시 조회 페이지                                               //
//                                                                                                                //
//  API ENDPOINTS:                                                                                                //
//                                                                                                                //
//  1. GET /api/mathcepts/temp                                                                                    //
//     - 임시 매스컨셉 조회 및 검색용 API                                                                          //
//     - Request Params:                                                                                          //
//       • description='true' (선택) - description 포함 여부                                                       //
//       • math_type: ["AL", "AN", "GE", "PS"] 중 택1                                                            //
//       • concept_type: ["DEF", "AXM", "IDN", "THM", "MTH", "PRP", "FOR", "NTN"] 중 택1                        //
//       • concept_name: 검색할 개념명 (예: "거듭")                                                                //
//                                                                                                                //
//  2. GET /api/mathcepts/temp/by-concept-ids                                                                    //
//     - 매스컨셉 ID로 조회                                                                                        //
//     - Request Params:                                                                                          //
//       • concept_ids: 쉼표로 구분된 Integer 문자열 (필수) 예: "5,55,555"                                         //
//     - Response: descriptions 필드 포함                                                                         //
//                                                                                                                //
//  3. GET /api/mathcepts/temp/tags                                                                              //
//     - 태그로 매스컨셉 조회                                                                                      //
//     - Request Params:                                                                                          //
//       • description='true' (선택) - description 포함 여부                                                       //
//       • tags: 검색할 태그명 (필수, 2자 이상 100자 미만, DB 문자열과 정확히 일치해야 함)                           //
//                                                                                                                //
//  ENUM DEFINITIONS:                                                                                             //
//                                                                                                                //
//  Math_type (수학 분야):                                                                                        //
//    • AL: 대수학 (Algebra)                                                                                      //
//    • AN: 해석학 (Analysis)                                                                                     //
//    • GE: 기하학 (Geometry)                                                                                     //
//    • PS: 확률과통계 (Probability and Statistics)                                                               //
//                                                                                                                //
//  Concept_types (개념 유형):                                                                                    //
//    • DEF: 정의 (Definition)                                                                                    //
//    • AXM: 공리 (Axiom)                                                                                         //
//    • IDN: 정리 (Identity)                                                                                      //
//    • THM: 이론 (Theorem)                                                                                       //
//    • MTH: 방법론 (Method)                                                                                      //
//    • PRP: 성질 (Property)                                                                                      //
//    • FOR: 공식 (Formula)                                                                                       //
//    • NTN: 표기법 (Notation)                                                                                    //
//                                                                                                                //
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button, IsoMathceptDetailModal, useIsoMathceptDetail, BypassLayout } from '../components/common';
import { Search, Loader, Download, ChevronLeft, ChevronRight, Plus, X, Check, Layers } from 'lucide-react';
import KMatrixContent from '../components/KMatrixContent';
import { Modal } from '../components/common';
import { useNavigate, useSearchParams as useRouterSearchParams } from 'react-router-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
    'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
    'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
    'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
    'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
    'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
    'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
    'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
};

// math_type에 따른 색상 매핑 (다른 색조로 구분)
const getMathTypeColor = (type) => {
  const colors = {
    'AL': 'rgba(99, 102, 241, 0.5)',    // 인디고 (대수학)
    'AN': 'rgba(244, 63, 94, 0.5)',     // 로즈 (해석학)
    'GE': 'rgba(16, 185, 129, 0.5)',    // 에메랄드 (기하학)
    'PS': 'rgba(168, 85, 247, 0.5)',    // 퍼플 (확률과통계)
  };
  return colors[type] || 'rgba(107, 114, 128, 0.5)'; // 기본 회색
};

function MathConcept() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // URL 기반 상태 관리
  const [urlSearchParams, setUrlSearchParams] = useRouterSearchParams();

  // URL에서 파라미터 읽기
  const getUrlParams = useCallback(() => {
    return {
      page: parseInt(urlSearchParams.get('page')) || 1,
      math_type: urlSearchParams.get('math_type') || '',
      concept_type: urlSearchParams.get('concept_type') || '',
      concept_name: urlSearchParams.get('concept_name') || '',
      tag: urlSearchParams.get('tag') || ''
    };
  }, [urlSearchParams]);

  // URL 파라미터 업데이트 함수
  const updateUrlParams = useCallback((params) => {
    const newParams = new URLSearchParams();

    // 페이지가 1이 아닌 경우에만 URL에 포함
    if (params.page && params.page > 1) {
      newParams.set('page', String(params.page));
    }
    // 검색 파라미터가 있는 경우에만 URL에 포함
    if (params.math_type) newParams.set('math_type', params.math_type);
    if (params.concept_type) newParams.set('concept_type', params.concept_type);
    if (params.concept_name) newParams.set('concept_name', params.concept_name);
    if (params.tag) newParams.set('tag', params.tag);

    setUrlSearchParams(newParams);
  }, [setUrlSearchParams]);

  const [loading, setLoading] = useState(false);
  const [mathcepts, setMathcepts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // K 행렬 모달 상태
  const [showKMatrixModal, setShowKMatrixModal] = useState(false);

  // URL에서 파생된 상태
  const urlParams = getUrlParams();
  const currentPage = urlParams.page;
  const [pageInputValue, setPageInputValue] = useState(String(urlParams.page));

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  // 스크롤 컨테이너 ref와 타이머
  const scrollContainerRef = React.useRef(null);
  const scrollTimeoutRef = React.useRef(null);

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 스크롤 중 클래스 추가
    container.classList.add('is-scrolling');

    // 이전 타이머 클리어
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // 스크롤 종료 후 클래스 제거
    scrollTimeoutRef.current = setTimeout(() => {
      container.classList.remove('is-scrolling');
    }, 1000);
  }, []);

  // 개념 등록 모달 상태
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    concept_name: '',
    math_type: 'AL',
    concept_type: 'DEF',
    descriptions: '',
    order: ''
  });

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = useCallback(() => {
    try {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const currentIsMobile = windowWidth < 768;

      // 여유분(buffer) 추가: border, gap, 브라우저 렌더링 차이 등 고려
      const buffer = 10;

      let availableHeight, rowHeight;

      if (currentIsMobile) {
        // ============ 모바일 (창 크기 768px 미만) ============
        // BypassLayout은 창 크기만 보고 MobileNav 표시 여부 결정
        // BypassLayout 제공 영역: windowHeight - Header(50) - MobileNav(68)
        const bypassLayoutHeight = windowHeight - 50 - 68;
        // 카드 영역: BypassLayout - margin(29)
        const cardHeight = bypassLayoutHeight - 29;
        // 카드 내부 고정 요소: 카드헤더(130: padding 24 + title 32 + filters 72) + marginTop(8) + 테이블헤더(30) + 페이지네이션(35)
        const cardInternalFixed = 130 + 8 + 30 + 35 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 38; // py-2(16px) + badge(16px) + 여유 = ~38px

      } else {
        // ============ 데스크톱 ============
        // 카드 높이: calc(100vh - 6rem) = windowHeight - 96px
        const cardHeight = windowHeight - 96;
        // 카드 내부 고정 요소: padding(32) + 타이틀(28) + 필터(70) + marginTop(16) + 테이블헤더(40) + 페이지네이션(60)
        const cardInternalFixed = 32 + 28 + 70 + 16 + 40 + 60 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 40; // py-2(16px) + badge(18px) + 여유 = ~40px
      }

      const itemsCount = Math.floor(availableHeight / rowHeight);

      // 최소/최대 개수 제한
      const minItems = 5;
      const maxItems = currentIsMobile ? 30 : 50;

      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 20; // 기본값
    }
  }, []);

  // 화면 크기 변경 감지 및 itemsPerPage 재계산
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobileView(newIsMobile);
      setItemsPerPage(calculateItemsPerPage());
    };

    // 초기 계산
    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [calculateItemsPerPage]);

  // 상세 정보 모달 (공통 컴포넌트 사용)
  const { isModalOpen: detailModalOpen, loading: detailLoading, data: detailData, fetchAndShow: fetchMathceptDetails, closeModal: closeDetailModal, refreshData: refreshDetailData } = useIsoMathceptDetail({ handleAuth: true });

  // 폼 입력 상태 (로컬 - 입력 중에는 URL 업데이트 안함)
  const [formState, setFormState] = useState({
    math_type: urlParams.math_type,
    concept_type: urlParams.concept_type,
    concept_name: urlParams.concept_name,
    tag: urlParams.tag
  });

  // URL 변경 시 폼 상태 동기화
  useEffect(() => {
    setFormState({
      math_type: urlParams.math_type,
      concept_type: urlParams.concept_type,
      concept_name: urlParams.concept_name,
      tag: urlParams.tag
    });
  }, [urlParams.math_type, urlParams.concept_type, urlParams.concept_name, urlParams.tag]);

  // 검색 필터 상태 (URL에서 파생 - 실제 검색에 사용)
  const searchParams = useMemo(() => ({
    description: false,
    math_type: urlParams.math_type,
    concept_type: urlParams.concept_type,
    concept_name: urlParams.concept_name,
    tag: urlParams.tag
  }), [urlParams.math_type, urlParams.concept_type, urlParams.concept_name, urlParams.tag]);


  // 옵션 정의
  const mathTypeOptions = [
    { value: '', label: '전체' },
    { value: 'AL', label: '대수학 (AL)' },
    { value: 'AN', label: '해석학 (AN)' },
    { value: 'GE', label: '기하학 (GE)' },
    { value: 'PS', label: '확률과통계 (PS)' }
  ];

  const conceptTypeOptions = [
    { value: '', label: '전체' },
    { value: 'DEF', label: '정의 (DEF)' },
    { value: 'AXM', label: '공리 (AXM)' },
    { value: 'IDN', label: '정리 (IDN)' },
    { value: 'THM', label: '이론 (THM)' },
    { value: 'MTH', label: '방법론 (MTH)' },
    { value: 'PRP', label: '성질 (PRP)' },
    { value: 'FOR', label: '공식 (FOR)' },
    { value: 'NTN', label: '표기법 (NTN)' }
  ];

  // LaTeX 렌더링 함수
  const renderLatex = useCallback((text) => {
    if (!text) return '';

    try {
      const latexPattern = /\$([^$]+)\$/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = latexPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        const latex = match[1];
        const html = katex.renderToString(latex, {
          throwOnError: false,
          displayMode: false
        });
        parts.push(`<span class="katex-inline">${html}</span>`);

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.join('');
    } catch (error) {
      console.error('LaTeX rendering error:', error);
      return text;
    }
  }, []);

  // API 호출
  const fetchMathcepts = useCallback(async (customParams = null) => {
    try {
      setLoading(true);

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const currentParams = customParams || searchParams;

      if (currentParams.tag) {
        const params = new URLSearchParams();
        if (currentParams.description) {
          params.append('description', 'true');
        }
        params.append('tags', currentParams.tag);

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/list/tags?${params}`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setMathcepts(data.data || []);
          setTotalCount(data.data?.length || 0);
        } else {
          console.error('Failed to fetch mathcepts by tag:', response.status);
          setMathcepts([]);
          setTotalCount(0);
        }
      } else {
        const params = new URLSearchParams();
        if (currentParams.description) {
          params.append('description', 'true');
        }
        if (currentParams.math_type) {
          params.append('math_type', currentParams.math_type);
        }
        if (currentParams.concept_type) {
          params.append('concept_type', currentParams.concept_type);
        }
        if (currentParams.concept_name) {
          params.append('concept_name', currentParams.concept_name);
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/list?${params}`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setMathcepts(data.data || []);
          setTotalCount(data.data?.length || 0);
        } else {
          console.error('Failed to fetch mathcepts:', response.status);
          setMathcepts([]);
          setTotalCount(0);
        }
      }
    } catch (error) {
      console.error('Error fetching mathcepts:', error);
      setMathcepts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, searchParams, navigate]);

  // 개념 등록 API 함수
  const registerMathcept = useCallback(async () => {
    // 필수 필드 검증
    if (!registerForm.concept_name.trim()) {
      alert('개념명을 입력해주세요.');
      return;
    }
    if (!registerForm.math_type) {
      alert('수학분야를 선택해주세요.');
      return;
    }
    if (!registerForm.concept_type) {
      alert('개념유형을 선택해주세요.');
      return;
    }

    try {
      setRegisterLoading(true);

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 요청 데이터 구성
      const requestData = {
        concept_name: registerForm.concept_name.trim(),
        math_type: registerForm.math_type,
        concept_type: registerForm.concept_type
      };

      // 선택적 필드 추가
      if (registerForm.descriptions && registerForm.descriptions.trim()) {
        requestData.descriptions = registerForm.descriptions.trim();
      }
      if (registerForm.order && !isNaN(parseInt(registerForm.order, 10))) {
        requestData.order = parseInt(registerForm.order, 10);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/register`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (response.ok) {
        await response.json();
        alert('매스컨셉이 성공적으로 등록되었습니다.');
        // 모달 닫기 및 폼 초기화
        setRegisterModalOpen(false);
        setRegisterForm({
          concept_name: '',
          math_type: 'AL',
          concept_type: 'DEF',
          descriptions: '',
          order: ''
        });
        // 목록 새로고침
        fetchMathcepts();
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.message) {
          alert(`등록 실패: ${errorData.message}`);
        } else {
          alert(`매스컨셉 등록에 실패했습니다. (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error registering mathcept:', error);
      alert('매스컨셉 등록 중 오류가 발생했습니다.');
    } finally {
      setRegisterLoading(false);
    }
  }, [registerForm, getAccessToken, navigate, fetchMathcepts]);

  // URL 변경 시 데이터 로드 및 페이지 입력값 동기화
  useEffect(() => {
    setPageInputValue(String(currentPage));
    fetchMathcepts({
      description: false,
      math_type: urlParams.math_type,
      concept_type: urlParams.concept_type,
      concept_name: urlParams.concept_name,
      tag: urlParams.tag
    });
  }, [urlSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // 검색 핸들러 (현재 폼 상태 값으로 URL 업데이트)
  const handleSearch = () => {
    updateUrlParams({
      page: 1,
      math_type: formState.math_type,
      concept_type: formState.concept_type,
      concept_name: formState.concept_name,
      tag: formState.tag
    });
  };

  // 초기화 핸들러
  const handleReset = () => {
    setFormState({
      math_type: '',
      concept_type: '',
      concept_name: '',
      tag: ''
    });
    updateUrlParams({
      page: 1,
      math_type: '',
      concept_type: '',
      concept_name: '',
      tag: ''
    });
  };

  // 엔터키 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      handleReset();
      e.target.blur(); // 입력 필드에서 포커스 해제
    }
  };

  // 페이지네이션된 데이터
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return mathcepts.slice(startIndex, endIndex);
  }, [mathcepts, currentPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateUrlParams({
        page: newPage,
        math_type: searchParams.math_type,
        concept_type: searchParams.concept_type,
        concept_name: searchParams.concept_name,
        tag: searchParams.tag
      });
    }
  };

  // CSV 다운로드
  const handleDownloadCSV = () => {
    const headers = ['ID', '개념명', '수학분야', '개념유형', '태그', '순서', '상태', '생성일'];
    const rows = mathcepts.map(item => {
      const mathType = mathTypeOptions.find(opt => opt.value === item.math_type)?.label || item.math_type;
      const conceptType = conceptTypeOptions.find(opt => opt.value === item.concept_type)?.label || item.concept_type;
      return [
        item.concept_id,
        item.concept_name,
        mathType,
        conceptType,
        item.tags?.join(';') || '',
        item.order,
        item.is_outdated ? '구버전' : '최신',
        new Date(item.created_at).toLocaleDateString('ko-KR')
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mathcepts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // 행 클릭 핸들러
  const handleRowClick = (row) => {
    if (row.concept_id) {
      fetchMathceptDetails([row.concept_id]);
    }
  };

  // 태그 클릭 핸들러
  const handleTagClick = (tag) => {
    updateUrlParams({
      page: 1,
      math_type: '',
      concept_type: '',
      concept_name: '',
      tag: tag
    });
    closeDetailModal();
  };

  // 수학분야 클릭 핸들러
  const handleMathTypeClick = (mathType) => {
    updateUrlParams({
      page: 1,
      math_type: mathType,
      concept_type: '',
      concept_name: '',
      tag: ''
    });
    closeDetailModal();
  };

  // 개념유형 클릭 핸들러
  const handleConceptTypeClick = (conceptType) => {
    updateUrlParams({
      page: 1,
      math_type: '',
      concept_type: conceptType,
      concept_name: '',
      tag: ''
    });
    closeDetailModal();
  };

  // 페이지 번호 렌더링 함수 (StudentManagement 스타일)
  const renderPageNumbers = (isMobileView) => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(start + maxVisible - 1, totalPages);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (isMobileView) {
      // 모바일 뷰
      if (start > 1) {
        pageNumbers.push(
          <button
            key={1}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );
        if (start > 2) {
          pageNumbers.push(<span key="ellipsis1" className="px-1 text-gray-400">...</span>);
        }
      }

      for (let i = start; i <= end; i++) {
        if (currentPage === i) {
          const inputWidth = Math.max(2, String(pageInputValue).length * 0.6 + 1.2);
          pageNumbers.push(
            <input
              key={i}
              type="text"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPageInputValue(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newPage = parseInt(pageInputValue);
                  if (newPage >= 1 && newPage <= totalPages) {
                    e.target.blur();
                    setTimeout(() => {
                      handlePageChange(newPage);
                    }, 100);
                  } else {
                    setPageInputValue(String(currentPage));
                  }
                }
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => setPageInputValue(String(currentPage))}
              className="flex items-center justify-center rounded-full border border-primary bg-primary text-white text-xs font-bold"
              style={{ width: `${inputWidth}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }}
            />
          );
        } else {
          pageNumbers.push(
            <button
              key={i}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
              onClick={() => handlePageChange(i)}
            >
              {i}
            </button>
          );
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pageNumbers.push(<span key="ellipsis2" className="px-1 text-gray-400">...</span>);
        }
        pageNumbers.push(
          <button
            key={totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }
    } else {
      // 데스크톱 뷰
      if (start > 1) {
        pageNumbers.push(
          <button
            key={1}
            className="page-button"
            style={{ borderRadius: '1.5rem' }}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );
        if (start > 2) {
          pageNumbers.push(<span key="ellipsis1" className="page-dots">...</span>);
        }
      }

      for (let i = start; i <= end; i++) {
        if (currentPage === i) {
          const inputWidth = Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
          pageNumbers.push(
            <input
              key={i}
              type="text"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPageInputValue(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newPage = parseInt(pageInputValue);
                  if (newPage >= 1 && newPage <= totalPages) {
                    e.target.blur();
                    setTimeout(() => {
                      handlePageChange(newPage);
                    }, 100);
                  } else {
                    setPageInputValue(String(currentPage));
                  }
                }
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => setPageInputValue(String(currentPage))}
              className="page-button active font-bold"
              style={{
                borderRadius: '1.5rem',
                width: `${inputWidth}rem`,
                minWidth: '2.5rem',
                height: '40px',
                textAlign: 'center',
                outline: 'none',
                border: 'none',
                cursor: 'text',
                padding: '0 8px'
              }}
            />
          );
        } else {
          pageNumbers.push(
            <button
              key={i}
              className="page-button"
              style={{ borderRadius: '1.5rem' }}
              onClick={() => handlePageChange(i)}
            >
              {i}
            </button>
          );
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pageNumbers.push(<span key="ellipsis2" className="page-dots">...</span>);
        }
        pageNumbers.push(
          <button
            key={totalPages}
            className="page-button"
            style={{ borderRadius: '1.5rem' }}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }
    }

    return pageNumbers;
  };

  return (
    <BypassLayout
      loading={loading && mathcepts.length === 0}
      renderContent={() => (
        <>
          {/* 스크롤바 스타일 */}
          <style>{`
            .mathconcept-scroll-container {
              scrollbar-width: thin;
              scrollbar-color: transparent transparent;
            }
            .mathconcept-scroll-container::-webkit-scrollbar {
              height: 6px;
              width: 6px;
              background: transparent;
            }
            .mathconcept-scroll-container::-webkit-scrollbar-track {
              background: transparent;
            }
            .mathconcept-scroll-container::-webkit-scrollbar-thumb {
              background: transparent;
              border-radius: 3px;
              transition: background 0.2s ease;
            }
            .mathconcept-scroll-container:hover::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.2);
            }
            .mathconcept-scroll-container::-webkit-scrollbar-thumb:hover {
              background: rgba(0, 0, 0, 0.4);
            }
            .mathconcept-scroll-container.is-scrolling::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.3);
            }
          `}</style>
          {/* 메인 카드 컨테이너 */}
          <div
            className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
              isMobileView ? 'flex-1' : 'h-[calc(100vh-6rem)] w-full'
            }`}
            style={{
              width: isMobileView ? 'calc(100% - 28.8px)' : '100%',
              height: isMobileView ? 'calc(100% - 28.8px)' : '100%',
              margin: isMobileView ? '14.4px' : '0'
            }}
          >
            {/* 헤더 영역 */}
            <div className="shrink-0 border-b">
              <div style={{
                padding: isMobileView ? '12px' : '16px',
                marginBottom: isMobileView ? '0' : '0'
              }}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">매스컨셉</h3>
                    <p className="text-xs text-gray-500">
                      - 총 <span className="font-semibold text-primary">{totalCount}</span>건 중 <span className="font-semibold text-primary">{paginatedData.length}</span>건 표시 중
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowKMatrixModal(true)}
                      rounded="3xl"
                    >
                      <Layers size={14} />
                      {!isMobileView && 'K 행렬'}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setRegisterModalOpen(true)}
                      rounded="3xl"
                    >
                      <Plus size={14} />
                      {!isMobileView && '등록'}
                    </Button>
                    {totalCount > 0 && (
                      <Button variant="secondary" size="sm" onClick={handleDownloadCSV} rounded="3xl">
                        <Download size={14} />
                        {!isMobileView && 'CSV'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* 필터 영역 */}
                <div className="space-y-3">
                  {isMobileView ? (
                    /* 모바일 필터 레이아웃 */
                    <div>
                      <div className="flex gap-2">
                        <div className="flex-1 grid gap-2 grid-cols-2">
                          <input
                            type="text"
                            placeholder="개념명"
                            value={formState.concept_name}
                            onChange={(e) => setFormState({ ...formState, concept_name: e.target.value })}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1.5 text-xs"
                          />
                          <select
                            value={formState.math_type}
                            onChange={(e) => setFormState({ ...formState, math_type: e.target.value })}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1.5 text-xs bg-white"
                          >
                            {mathTypeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formState.concept_type}
                            onChange={(e) => setFormState({ ...formState, concept_type: e.target.value })}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1.5 text-xs bg-white"
                          >
                            {conceptTypeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="태그"
                            value={formState.tag}
                            onChange={(e) => setFormState({ ...formState, tag: e.target.value })}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1.5 text-xs"
                          />
                        </div>
                        <div className="flex w-14 flex-col gap-2">
                          <Button
                            onClick={handleReset}
                            variant="secondary"
                            size="sm"
                            rounded="3xl"
                            className="flex-1 min-h-[28px] text-xs px-1"
                          >
                            초기화
                          </Button>
                          <Button
                            onClick={handleSearch}
                            variant="primary"
                            size="sm"
                            rounded="3xl"
                            className="flex-1 min-h-[28px] text-xs px-1"
                            disabled={loading}
                          >
                            <Search size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* 데스크톱 필터 레이아웃 */
                    <div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1 flex gap-2">
                          <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">개념명</label>
                            <input
                              type="text"
                              placeholder="검색어 입력"
                              value={formState.concept_name}
                              onChange={(e) => setFormState({ ...formState, concept_name: e.target.value })}
                              onKeyDown={handleKeyPress}
                              className="w-full rounded border px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">수학 분야</label>
                            <select
                              value={formState.math_type}
                              onChange={(e) => setFormState({ ...formState, math_type: e.target.value })}
                              onKeyDown={handleKeyPress}
                              className="w-full rounded border px-3 py-2 text-sm bg-white"
                            >
                              {mathTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">개념 유형</label>
                            <select
                              value={formState.concept_type}
                              onChange={(e) => setFormState({ ...formState, concept_type: e.target.value })}
                              onKeyDown={handleKeyPress}
                              className="w-full rounded border px-3 py-2 text-sm bg-white"
                            >
                              {conceptTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">태그</label>
                            <input
                              type="text"
                              placeholder="태그명 입력"
                              value={formState.tag}
                              onChange={(e) => setFormState({ ...formState, tag: e.target.value })}
                              onKeyDown={handleKeyPress}
                              className="w-full rounded border px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleReset}
                            variant="secondary"
                            size="sm"
                            rounded="3xl"
                            className="px-4"
                          >
                            초기화
                          </Button>
                          <Button
                            onClick={handleSearch}
                            variant="primary"
                            size="sm"
                            rounded="3xl"
                            className="px-4"
                            disabled={loading}
                          >
                            <Search size={14} />
                            검색
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 컨텐츠 영역 */}
            <div className="flex-1 overflow-y-auto" style={{
              padding: '0',
              marginTop: isMobileView ? '8px' : '16px',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0
            }}>
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="animate-spin" size={32} />
                </div>
              ) : totalCount > 0 ? (
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 mathconcept-scroll-container"
                  style={{
                    minHeight: 0,
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'auto'
                  }}
                >
                  <table className="min-w-full" style={{ width: 'max-content', minWidth: '100%' }}>
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          개념번호
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          ID
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          개념명
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          수학분야
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          개념유형
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          태그
                        </th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                          생성일
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {paginatedData.map((item) => {
                        const mathType = mathTypeOptions.find(opt => opt.value === item.math_type);
                        const conceptType = conceptTypeOptions.find(opt => opt.value === item.concept_type);

                        return (
                          <tr
                            key={item.concept_id}
                            onClick={() => handleRowClick(item)}
                            className="hover:bg-primary/10 cursor-pointer transition-colors duration-150 border-b border-gray-100"
                          >
                            <td className={`${isMobileView ? 'px-1 rounded-l-xl' : 'px-3 rounded-l-3xl'} py-2 whitespace-nowrap text-center transition-colors duration-150`}>
                              <span
                                className={`inline-flex items-center justify-center font-semibold ${item.is_outdated ? 'text-red-700' : 'text-green-700'}`}
                                style={{
                                  backgroundColor: item.is_outdated ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)',
                                  borderRadius: '6px',
                                  width: isMobileView ? '24px' : '28px',
                                  height: isMobileView ? '16px' : '18px',
                                  minWidth: isMobileView ? '24px' : '28px',
                                  fontSize: isMobileView ? '9px' : '10px'
                                }}
                                title={`순서: ${item.order} (${item.is_outdated ? '구버전' : '최신'})`}
                              >
                                {item.order}
                              </span>
                            </td>
                            <td className={`${isMobileView ? 'px-1' : 'px-3'} py-2 whitespace-nowrap text-center transition-colors duration-150`}>
                              <span className={`${isMobileView ? 'text-xs' : 'text-sm'} text-gray-600`}>
                                {item.concept_id}
                              </span>
                            </td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 transition-colors duration-150`}>
                              <div
                                className={`${isMobileView ? 'text-xs' : 'text-sm'} whitespace-nowrap`}
                                dangerouslySetInnerHTML={{ __html: renderLatex(item.concept_name) }}
                              />
                            </td>
                            <td className={`${isMobileView ? 'px-1' : 'px-3'} py-2 whitespace-nowrap text-center transition-colors duration-150`}>
                              <span
                                className="inline-flex items-center justify-center font-semibold text-gray-800 cursor-pointer hover:scale-110 hover:shadow-md transition-all duration-150"
                                style={{
                                  backgroundColor: getMathTypeColor(item.math_type),
                                  borderRadius: '6px',
                                  width: isMobileView ? '24px' : '28px',
                                  height: isMobileView ? '16px' : '18px',
                                  minWidth: isMobileView ? '24px' : '28px',
                                  fontSize: isMobileView ? '8px' : '9px'
                                }}
                                title={`${mathType ? mathType.label : item.math_type} - 클릭하여 검색`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMathTypeClick(item.math_type);
                                }}
                              >
                                {item.math_type}
                              </span>
                            </td>
                            <td className={`${isMobileView ? 'px-1' : 'px-3'} py-2 whitespace-nowrap text-center transition-colors duration-150`}>
                              <span
                                className="inline-flex items-center justify-center font-semibold text-gray-800 cursor-pointer hover:scale-110 hover:shadow-md transition-all duration-150"
                                style={{
                                  backgroundColor: getConceptTypeColor(item.concept_type),
                                  borderRadius: '6px',
                                  width: isMobileView ? '24px' : '28px',
                                  height: isMobileView ? '16px' : '18px',
                                  minWidth: isMobileView ? '24px' : '28px',
                                  fontSize: isMobileView ? '8px' : '9px'
                                }}
                                title={`${conceptType ? conceptType.label : item.concept_type} - 클릭하여 검색`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConceptTypeClick(item.concept_type);
                                }}
                              >
                                {item.concept_type}
                              </span>
                            </td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 transition-colors duration-150`}>
                              <div className={`flex gap-1 flex-wrap ${isMobileView ? '' : 'max-w-[200px]'}`}>
                                {item.tags?.slice(0, isMobileView ? 2 : 3).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className={`${isMobileView ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTagClick(tag);
                                    }}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {item.tags?.length > (isMobileView ? 2 : 3) && (
                                  <span className={`${isMobileView ? 'text-[10px]' : 'text-xs'} text-gray-400`}>+{item.tags.length - (isMobileView ? 2 : 3)}</span>
                                )}
                              </div>
                            </td>
                            <td className={`${isMobileView ? 'px-2 rounded-r-xl' : 'px-3 rounded-r-3xl'} py-2 whitespace-nowrap text-center ${isMobileView ? 'text-xs' : 'text-sm'} transition-colors duration-150`}>
                              {new Date(item.created_at).toLocaleDateString('ko-KR')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  검색 조건을 설정하고 검색 버튼을 클릭하세요.
                </div>
              )}

              {/* 페이지네이션 */}
              {!loading && totalCount > itemsPerPage && (
                <div className="shrink-0 bg-white py-3">
                  <div className={isMobileView ? "flex justify-center items-center gap-2" : "desktop-pagination"}>
                    <button
                      className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                      style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      {isMobileView ? '<' : <ChevronLeft size={16} />}
                    </button>

                    <div className="flex gap-1">
                      {renderPageNumbers(isMobileView)}
                    </div>

                    <button
                      className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                      style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      {isMobileView ? '>' : <ChevronRight size={16} />}
                    </button>
                  </div>
                </div>
              )}

              {!loading && totalCount === 0 && mathcepts.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  데이터가 없습니다.
                </div>
              )}
            </div>
          </div>

          {/* KaTeX 스타일 */}
          <style>{`
            .katex-inline {
              display: inline-block;
            }
            .katex-inline .katex {
              font-size: 1em;
            }
            .desktop-pagination {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 8px;
            }
            .page-button {
              min-width: 40px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid #e5e7eb;
              background: white;
              color: #374151;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .page-button:hover:not(:disabled) {
              background: #f3f4f6;
            }
            .page-button:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            .page-button.active {
              background: #4ABC85;
              color: white;
              border-color: #4ABC85;
            }
            .page-dots {
              padding: 0 4px;
              color: #9ca3af;
            }
          `}</style>

          {/* 매스컨셉 상세 정보 모달 */}
          <IsoMathceptDetailModal
            isOpen={detailModalOpen}
            onClose={closeDetailModal}
            data={detailData}
            loading={detailLoading}
            renderLatex={renderLatex}
            onTagClick={handleTagClick}
            showCloseButton={true}
            useLoader={true}
            handleAuth={true}
            onDataUpdate={() => {
              refreshDetailData();
              fetchMathcepts();
            }}
          />

          {/* 개념 등록 모달 */}
          <Modal
            isOpen={registerModalOpen}
            onClose={() => {
              setRegisterModalOpen(false);
              setRegisterForm({
                concept_name: '',
                math_type: 'AL',
                concept_type: 'DEF',
                descriptions: '',
                order: ''
              });
            }}
            title="새 매스컨셉 등록"
            size="md"
          >
            <div className="space-y-4">
              {/* 개념명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  개념명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={registerForm.concept_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, concept_name: e.target.value })}
                  placeholder="개념명을 입력하세요 (LaTeX 문법 지원: $수식$)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* 수학분야 & 개념유형 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수학분야 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={registerForm.math_type}
                    onChange={(e) => setRegisterForm({ ...registerForm, math_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                  >
                    {mathTypeOptions.filter(opt => opt.value !== '').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    개념유형 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={registerForm.concept_type}
                    onChange={(e) => setRegisterForm({ ...registerForm, concept_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                  >
                    {conceptTypeOptions.filter(opt => opt.value !== '').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 개념번호 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  개념번호 (선택)
                </label>
                <input
                  type="number"
                  value={registerForm.order}
                  onChange={(e) => setRegisterForm({ ...registerForm, order: e.target.value })}
                  placeholder="개념번호 (미입력시 자동 할당)"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명 (선택)
                </label>
                <textarea
                  value={registerForm.descriptions}
                  onChange={(e) => setRegisterForm({ ...registerForm, descriptions: e.target.value })}
                  placeholder="개념에 대한 설명을 입력하세요 (LaTeX 문법 지원: $수식$)"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>

              {/* 버튼 영역 */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRegisterModalOpen(false);
                    setRegisterForm({
                      concept_name: '',
                      math_type: 'AL',
                      concept_type: 'DEF',
                      descriptions: '',
                      order: ''
                    });
                  }}
                  disabled={registerLoading}
                >
                  <X size={16} />
                  취소
                </Button>
                <Button
                  variant="primary"
                  onClick={registerMathcept}
                  disabled={registerLoading}
                >
                  {registerLoading ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      등록
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Modal>

          {/* K 행렬 모달 */}
          <Modal
            isOpen={showKMatrixModal}
            onClose={() => setShowKMatrixModal(false)}
            title="K 행렬 보기"
            size="max"
            maxHeight="95vh"
            fixedHeight
            bodyClassName="flex flex-col min-h-0 overflow-hidden"
            footer={
              <Button
                variant="secondary"
                onClick={() => setShowKMatrixModal(false)}
              >
                닫기
              </Button>
            }
          >
            <KMatrixContent />
          </Modal>
        </>
      )}
    />
  );
}

export default MathConcept;
